import React, { useState, useEffect } from 'react';
import { Calendar, Clock, CheckCircle, XCircle, Package, User, MapPin, FileText, ChevronDown, ChevronRight, AlertCircle } from 'lucide-react';
import { useToast } from '../ui/ToastContext';
import ViewLayout from '../ui/ViewLayout';

const MyRequestsView = ({ 
  currentUser, 
  fetchReservations, 
  updateReservationStatus,
  props = [], // Add props to get current status
  onDataChange // Add callback to refresh main app data
}) => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('pending');
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedReservations, setExpandedReservations] = useState(new Set());

  // Load data when component mounts or user changes
  useEffect(() => {
    loadAllReservations();
  }, [currentUser]);

  // Load all reservations once
  const loadAllReservations = async () => {
    try {
      setLoading(true);
      console.log('MyRequestsView: Loading all reservations for:', {
        currentUser: currentUser,
        role: currentUser.role,
        organization: currentUser.organization
      });
      // Fetch all reservations (no status filter)
      const data = await fetchReservations(currentUser, null);
      console.log('MyRequestsView: Loaded all reservations:', data);
      setReservations(data);
    } catch (err) {
      console.error('Error loading reservations:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter reservations based on active tab
  const getFilteredReservations = () => {
    switch (activeTab) {
      case 'pending':
        return reservations.filter(r => r.status === 'pending');
      case 'approved':
        return reservations.filter(r => r.status === 'approved');
      case 'active':
        return reservations.filter(r => r.status === 'active_checkout');
      case 'history':
        return reservations.filter(r => 
          ['completed', 'rejected', 'cancelled'].includes(r.status)
        );
      default:
        return reservations;
    }
  };

  // Handle cancel reservation
  const handleCancelReservation = async (reservationId) => {
    if (!window.confirm('Are you sure you want to cancel this reservation request?')) {
      return;
    }
    
    try {
      console.log('🚫 Cancelling reservation:', reservationId);
      
      // Update reservation status to cancelled and free up props
      await updateReservationStatus(reservationId, 'cancelled', { 
        admin_notes: 'Cancelled by user' 
      });
      
      console.log('✅ Reservation cancelled and props released');
      await loadAllReservations();
      
      // Trigger main app data refresh to update prop statuses
      if (onDataChange) {
        console.log('🔄 Triggering main app data refresh after cancellation...');
        onDataChange();
      }
      
    } catch (err) {
      console.error('❌ Error cancelling reservation:', err);
      toast.error('Failed to cancel reservation', { title: 'Cancellation Error' });
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'active_checkout': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-purple-100 text-purple-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="h-4 w-4" />;
      case 'approved': return <CheckCircle className="h-4 w-4" />;
      case 'active_checkout': return <Package className="h-4 w-4" />;
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      case 'rejected': return <XCircle className="h-4 w-4" />;
      case 'cancelled': return <XCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  // Get user-friendly status text
  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Under Review';
      case 'approved': return 'Approved - Ready for Pickup';
      case 'active_checkout': return 'Checked Out';
      case 'completed': return 'Returned';
      case 'rejected': return 'Declined';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  // Format time to 12-hour format with AM/PM
  const formatTime = (timeString) => {
    if (!timeString) return '';
    
    // Handle various time formats
    const time = timeString.includes(':') ? timeString : timeString;
    
    try {
      // Parse the time string
      const [hours, minutes] = time.split(':');
      const hour24 = parseInt(hours, 10);
      const min = minutes || '00';
      
      // Convert to 12-hour format
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      const ampm = hour24 >= 12 ? 'PM' : 'AM';
      
      return `${hour12}:${min} ${ampm}`;
    } catch (error) {
      // If parsing fails, return original string
      return timeString;
    }
  };

  // Toggle reservation expansion
  const toggleReservationExpansion = (reservationId) => {
    const newExpanded = new Set(expandedReservations);
    if (newExpanded.has(reservationId)) {
      newExpanded.delete(reservationId);
    } else {
      newExpanded.add(reservationId);
    }
    setExpandedReservations(newExpanded);
  };

  const tabs = [
    { id: 'pending', label: 'Under Review', count: reservations.filter(r => r.status === 'pending').length },
    { id: 'approved', label: 'Ready for Pickup', count: reservations.filter(r => r.status === 'approved').length },
    { id: 'active', label: 'Checked Out', count: reservations.filter(r => r.status === 'active_checkout').length },
    { id: 'history', label: 'History', count: reservations.filter(r => ['completed', 'rejected', 'cancelled'].includes(r.status)).length }
  ];

  const stats = [
    <ViewLayout.Stat 
      title="Under Review" 
      value={reservations.filter(r => r.status === 'pending').length} 
      icon={Clock} 
      color="orange" 
    />,
    <ViewLayout.Stat 
      title="Ready" 
      value={reservations.filter(r => r.status === 'approved').length} 
      icon={CheckCircle} 
      color="green" 
    />,
    <ViewLayout.Stat 
      title="Checked Out" 
      value={reservations.filter(r => r.status === 'active_checkout').length} 
      icon={Package} 
      color="blue" 
    />,
    <ViewLayout.Stat 
      title="Total Requests" 
      value={reservations.length} 
      icon={Calendar} 
      color="purple" 
    />
  ];

  const filteredReservations = getFilteredReservations();

  return (
    <ViewLayout
      title="My Reservation Requests"
      subtitle={currentUser.organization}
      stats={stats}
    >
      <div className="space-y-6">
        {/* Responsive Tabs */}
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="-mb-px flex space-x-2 sm:space-x-8 min-w-max">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                {tab.count > 0 && (
                  <span className={`ml-1 sm:ml-2 py-0.5 px-1 sm:px-2 rounded-full text-xs ${
                    activeTab === tab.id ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

      {/* Loading state */}
      {loading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-gray-600">Loading your requests...</p>
        </div>
      )}

      {/* Reservations list - COMPACT VERSION */}
      {!loading && (
        <div className="space-y-2">
          {filteredReservations.length > 0 ? (
            filteredReservations.map(reservation => {
              const isExpanded = expandedReservations.has(reservation.id);
              return (
                <div key={reservation.id} className="bg-white rounded-lg shadow-sm border">
                  {/* Compact header - always visible */}
                  <div 
                    className="p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => toggleReservationExpansion(reservation.id)}
                  >
                    <div className="flex justify-between items-center gap-2">
                      <div className="flex items-center gap-2 flex-wrap min-w-0">
                        <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
                          getStatusColor(reservation.status)
                        }`}>
                          {getStatusIcon(reservation.status)}
                          <span className="hidden sm:inline">{getStatusText(reservation.status)}</span>
                        </div>
                        <div className="font-medium text-sm truncate">{reservation.target_org?.name || 'Unknown Warehouse'}</div>
                        <div className="text-xs text-gray-500 hidden sm:inline">•</div>
                        <div className="text-xs text-gray-500 flex-shrink-0">
                          {reservation.reservation_items?.length || 0} props
                        </div>
                        <div className="text-xs text-gray-500 hidden sm:inline">•</div>
                        <div className="text-xs text-gray-600 truncate hidden sm:block">{reservation.purpose || 'General Use'}</div>
                        <div className="text-xs text-gray-500 hidden sm:inline">•</div>
                        <div className="text-xs text-gray-500 hidden sm:block">
                          Requested {new Date(reservation.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                        {/* Action buttons based on status */}
                        {reservation.status === 'pending' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancelReservation(reservation.id);
                            }}
                            className="bg-gray-600 text-white px-2 py-1 rounded text-xs hover:bg-gray-700 whitespace-nowrap"
                          >
                            Cancel
                          </button>
                        )}
                        
                        {reservation.status === 'approved' && (
                          <div className="text-xs text-green-700 font-medium hidden sm:block">
                            Pickup: {new Date(reservation.approved_pickup_date).toLocaleDateString()}
                            {reservation.approved_pickup_time && ` at ${formatTime(reservation.approved_pickup_time)}`}
                          </div>
                        )}
                        
                        {reservation.status === 'completed' && (
                          <div className="text-xs text-purple-700 font-medium hidden sm:block">
                            Returned: {new Date(reservation.expected_return_date).toLocaleDateString()}
                          </div>
                        )}
                        
                        {/* Expand/collapse indicator */}
                        <div className="text-gray-400">
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expanded details - only visible when expanded */}
                  {isExpanded && (
                    <div className="px-3 pb-3 border-t border-gray-100">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 mt-3">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-gray-500" />
                          <div>
                            <div className="font-medium text-sm">Warehouse</div>
                            <div className="text-xs text-gray-600">{reservation.target_org?.name || 'Unknown'}</div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-gray-500" />
                          <div>
                            <div className="font-medium text-sm">Expected Return</div>
                            <div className="text-xs text-gray-600">
                              {new Date(reservation.expected_return_date).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-gray-500" />
                          <div>
                            <div className="font-medium text-sm">Purpose</div>
                            <div className="text-xs text-gray-600">{reservation.purpose || 'General Use'}</div>
                          </div>
                        </div>
                      </div>

                      {/* Props list with individual statuses */}
                      <div className="mb-3">
                        <h4 className="font-medium text-sm mb-2">Requested Props ({reservation.reservation_items?.length || 0}):</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                          {reservation.reservation_items?.map(item => {
                            // Find the current prop to get its live status
                            const currentProp = props.find(p => p.id === item.prop_id);
                            const propStatus = currentProp?.status || item.props?.status || 'unknown';
                            
                            const getStatusColor = (status) => {
                              switch(status) {
                                case 'available': return 'bg-green-100 text-green-800';
                                case 'checked_out': return 'bg-orange-100 text-orange-800';
                                case 'reserved': return 'bg-purple-100 text-purple-800';
                                case 'under_repair': return 'bg-red-100 text-red-800';
                                default: return 'bg-gray-100 text-gray-800';
                              }
                            };
                            
                            return (
                              <div key={item.prop_id} className="flex items-center gap-2 text-xs p-2 bg-gray-50 rounded">
                                <Package className="h-3 w-3 text-gray-500" />
                                <div className="flex-1">
                                  <span className="font-medium">{item.props?.name || `Prop ${item.prop_id}`}</span>
                                  <span className="text-gray-500"> • {item.props?.category}</span>
                                </div>
                                <span className={`px-1 py-0.5 rounded text-xs font-medium ${getStatusColor(propStatus)}`}>
                                  {propStatus}
                                </span>
                              </div>
                            );
                          }) || (
                            <div className="text-xs text-gray-500 col-span-full">No props listed</div>
                          )}
                        </div>
                      </div>

                      {/* Your original request details */}
                      <div className="mb-3 p-2 bg-blue-50 rounded">
                        <div className="font-medium text-xs mb-1">Your Request Details:</div>
                        <div className="text-xs text-blue-800 space-y-1">
                          <div>Preferred pickup: {reservation.preferred_pickup_day || 'Not specified'} {reservation.preferred_time_range && `(${reservation.preferred_time_range})`}</div>
                          {reservation.notes && <div>Notes: {reservation.notes}</div>}
                        </div>
                      </div>

                      {/* Status-specific information */}
                      {reservation.status === 'approved' && reservation.approved_pickup_date && (
                        <div className="mb-3 p-2 bg-green-50 rounded">
                          <div className="font-medium text-xs mb-1">✅ Approved for Pickup:</div>
                          <div className="text-xs text-green-700">
                            <div className="font-medium">
                              {new Date(reservation.approved_pickup_date).toLocaleDateString()}
                              {reservation.approved_pickup_time && ` at ${formatTime(reservation.approved_pickup_time)}`}
                            </div>
                            {reservation.admin_notes && (
                              <div className="mt-1">Instructions: {reservation.admin_notes}</div>
                            )}
                            <div className="mt-2 font-medium text-green-800">
                              📍 Visit {reservation.target_org?.name} at your scheduled time to collect your props!
                            </div>
                          </div>
                        </div>
                      )}

                      {reservation.status === 'active_checkout' && (
                        <div className="mb-3 p-2 bg-blue-50 rounded">
                          <div className="font-medium text-xs mb-1">📦 Currently Checked Out:</div>
                          <div className="text-xs text-blue-700">
                            <div>Please return these props by: <span className="font-medium">{new Date(reservation.expected_return_date).toLocaleDateString()}</span></div>
                            <div className="mt-1">Return to: {reservation.target_org?.name}</div>
                          </div>
                        </div>
                      )}

                      {reservation.status === 'completed' && (
                        <div className="mb-3 p-2 bg-purple-50 rounded">
                          <div className="font-medium text-xs mb-1">✅ Returned Successfully:</div>
                          <div className="text-xs text-purple-700">
                            <div>All props from this reservation have been returned.</div>
                            <div className="mt-1">Returned to: {reservation.target_org?.name}</div>
                          </div>
                        </div>
                      )}

                      {reservation.status === 'rejected' && reservation.admin_notes && (
                        <div className="p-2 bg-red-50 rounded">
                          <div className="font-medium text-xs mb-1">❌ Request Declined:</div>
                          <div className="text-xs text-red-700">{reservation.admin_notes}</div>
                        </div>
                      )}

                      {reservation.status === 'cancelled' && (
                        <div className="p-2 bg-gray-50 rounded">
                          <div className="font-medium text-xs mb-1">🚫 Request Cancelled</div>
                          <div className="text-xs text-gray-700">This request was cancelled and no props were reserved.</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="text-center py-12 text-gray-500">
              <Calendar className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg mb-2">No {activeTab.toLowerCase()} requests</p>
              <p className="text-sm">
                {activeTab === 'pending' 
                  ? 'Submit reservation requests from the prop search to see them here'
                  : activeTab === 'approved'
                  ? 'Approved requests ready for pickup will appear here'
                  : activeTab === 'active'
                  ? 'Props you have checked out will appear here'
                  : 'Completed, declined, and cancelled requests will appear here'
                }
              </p>
            </div>
          )}
        </div>
      )}
      </div>
    </ViewLayout>
  );
};

export default MyRequestsView;
